import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

const MIN_SHIFT_HEIGHT_MINUTES = 60
const HOUR_MS = 60 * 60 * 1000

/**
 * One day of the Shifts page: a half-open interval [start, end) in the configured timezone.
 * The same window is used to fetch the shifts (the backend returns shifts *starting* inside it)
 * and to draw the grid, so the two can't drift apart.
 */
export interface ShiftDayWindow {
  start: dayjs.Dayjs
  end: dayjs.Dayjs
}

/**
 * Build back-to-back day windows for every calendar date from startDate to endDate (inclusive).
 * Each window runs from `dayStart` on its date to `dayStart` on the next date in `tz`, so it is
 * 23 or 25 hours long on DST change days, and consecutive windows share their boundary.
 * @param startDate - First date, YYYY-MM-DD
 * @param endDate - Last date, YYYY-MM-DD
 * @param dayStart - Wall-clock time the day starts at (SCHEDULING_END_HOUR)
 * @param tz - IANA timezone the wall-clock time is interpreted in
 */
export const getShiftDayWindows = (
  startDate: string,
  endDate: string,
  dayStart: { hours: number; minutes: number },
  tz: string
): ShiftDayWindow[] => {
  const time = `${String(dayStart.hours).padStart(2, '0')}:${String(dayStart.minutes).padStart(2, '0')}`
  const boundaryOf = (date: dayjs.Dayjs) => dayjs.tz(`${date.format('YYYY-MM-DD')} ${time}`, tz)

  const first = dayjs(startDate)
  const days = dayjs(endDate).diff(first, 'day') + 1

  return Array.from({ length: Math.max(days, 0) }, (_, i) => ({
    start: boundaryOf(first.add(i, 'day')),
    end: boundaryOf(first.add(i + 1, 'day')),
  }))
}

const windowMinutes = (dayWindow: ShiftDayWindow) => dayWindow.end.diff(dayWindow.start, 'minute')

/**
 * Vertical position of a shift inside its day window, as CSS percentages.
 * Shifts running past the end of the window are cut off at the bottom.
 */
export const getShiftPosition =
  (dayWindow: ShiftDayWindow) =>
  (startDateTime: dayjs.Dayjs, endDateTime: dayjs.Dayjs): { top: string; height: string } => {
    const total = windowMinutes(dayWindow)
    const clamp = (minutes: number) => Math.max(0, Math.min(total, minutes))

    const posStart = clamp(startDateTime.diff(dayWindow.start, 'minute'))
    const posEnd = clamp(endDateTime.diff(dayWindow.start, 'minute'))

    // No overlap with timeline
    if (posEnd <= posStart) {
      return { top: '0%', height: '0%' }
    }

    const finalPosEnd = Math.min(total, Math.max(posEnd, posStart + MIN_SHIFT_HEIGHT_MINUTES))

    return {
      top: `${(posStart / total) * 100}%`,
      height: `${((finalPosEnd - posStart) / total) * 100}%`,
    }
  }

/**
 * Hourly grid lines for a day window, labelled with the local wall-clock time.
 * Lines are spaced by real elapsed time, so a DST day shows a repeated or skipped hour.
 */
export const getTimeSlots = (dayWindow: ShiftDayWindow, tz: string): { label: string; top: string }[] => {
  const startMs = dayWindow.start.valueOf()
  const totalMs = dayWindow.end.valueOf() - startMs
  const count = Math.floor(totalMs / HOUR_MS) + 1
  // Not dayjs: its timezone plugin mislabels the repeated hour after the autumn clock change
  const clock = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })

  return Array.from({ length: count }, (_, i) => ({
    label: clock.format(startMs + i * HOUR_MS),
    top: `${((i * HOUR_MS) / totalMs) * 100}%`,
  }))
}
